import { useEffect, useState } from 'react';
import { Box, Container, LinearProgress } from '@mui/material';
import useStore from '../../states/store';
import { useSearchParams } from 'react-router-dom';
// ICONS
import {
  CheckCircleOutline,
  Inventory2Outlined,
  LocalShippingOutlined,
  RemoveCircleOutline,
  CancelOutlined,
} from '@mui/icons-material';

// COMPONENTS
import Header from './components/Header';
import Cards from './components/Cards';
import Table from './components/Table';
import ImportModal from './components/ImportModal';
import NotFoundTemplate from '../../components/AppLayout/NotFoundTemplate';
// API
import {
  getPlanDetails,
  getFinalizeListPlan,
  postDeliveryImport,
  postDeliveryExport,
} from '../../api/productSamplingWorkflowApi';

import { toast } from 'react-toastify';

export default function AllocationStatusMonitoring() {
  const auth = useStore((state) => state.auth);
  const [searchParams] = useSearchParams();
  const plan_id = searchParams.get('plan_id');

  const [plans, setPlans] = useState({
    loading: true,
    items: [],
  });

  const [table, setTable] = useState({
    loading: true,
    show: false,
    items: [],
    details: {},
    productColumn: [],
  });
  const [selected, setSelected] = useState(null);
  const [importModal, setImportModal] = useState({ toggle: false });
  const [planNotFound, setPlanNotFound] = useState(false);

  const statusOptions = [
    {
      key: 'pending',
      value: 1,
      label: 'No status yet',
      color: '#78909C',
      icon: <RemoveCircleOutline />,
    },
    {
      key: 'preparing',
      value: 2,
      label: 'Preparing',
      color: '#F57C00',
      icon: <Inventory2Outlined color='warning' />,
    },
    {
      key: 'delivery',
      value: 3,
      label: 'For delivery',
      color: '#1976D2',
      icon: <LocalShippingOutlined color='primary' />,
    },
    {
      key: 'delivered',
      value: 4,
      label: 'Delivered',
      color: '#2E7D32',
      icon: <CheckCircleOutline color='success' />,
    },
    {
      key: 'cancelled',
      value: 5,
      label: 'Cancelled',
      color: '#D32F2F',
      icon: <CancelOutlined color='error' />,
    },
  ];

  const loadPlans = async () => {
    try {
      setPlans((prev) => ({
        ...prev,
        loading: true,
      }));

      const request = {
        month: null,
        year: null,
      };

      const response = await getFinalizeListPlan(request);
      const { code, data } = response.data;

      if (code === '200') {
        setPlans((prev) => ({
          ...prev,
          loading: false,
          items: data,
        }));
      }
    } catch (error) {
      toast.dismiss();
      toast.warning(error.message);
    }
  };

  const handleViewPlan = async () => {
    try {
      setPlanNotFound(false);
      setTable((prev) => ({
        ...prev,
        loading: true,
      }));
      toast.loading('Processing request please wait...');
      const request = {
        id: selected ?? plan_id,
      };

      const response = await getPlanDetails(request);
      const { code, plan, products, data } = response.data;

      if (code === '200' && data.length > 0) {
        setTable((prev) => ({
          ...prev,
          loading: false,
          show: true,
          items: data,
          details: plan,
          productColumn: products,
        }));
        toast.dismiss();
        return;
      }
      setPlanNotFound(true);
      setTable((prev) => ({
        ...prev,
        loading: false,
        show: false,
      }));
      toast.dismiss();
    } catch (error) {
      setPlanNotFound(true);
      setTable((prev) => ({
        ...prev,
        loading: false,
        show: false,
      }));
      toast.dismiss();
      toast.warning(error.message);
    }
  };

  const handleImport = async () => {
    setImportModal((prev) => ({ ...prev, toggle: true }));
  };
  const handleExport = async () => {
    const toastId = toast.loading('Exporting delivery status...');

    try {
      const request = {
        id: selected ?? plan_id,
      };

      const response = await postDeliveryExport(request);
      const { code, file_name, result } = response.data.response;

      if (code !== '200') {
        throw new Error('Unable to export delivery status.');
      }

      toast.success(`Exported successfully: ${file_name}`, {
        id: toastId,
      });

      window.location.href = result;
      toast.dismiss();
    } catch (error) {
      toast.dismiss();
      toast.error(error?.message || 'Failed to export delivery status.', {
        id: toastId,
      });
    }
  };
  useEffect(() => {
    if (auth) {
      loadPlans();
    }
  }, [auth]);

  useEffect(() => {
    if (selected !== null || plan_id !== null) {
      handleViewPlan();
    }
  }, [selected, plan_id]);

  return (
    <Container maxWidth={false}>
      <Box py={3}>
        <Header />
        {auth && (
          <Cards
            plans={plans}
            selected={selected}
            setSelected={setSelected}
            statusOptions={statusOptions}
          />
        )}
        {(selected !== null || plan_id !== null) &&
          (table.loading ? (
            <Box sx={{ width: '100%', mt: 3 }}>
              <LinearProgress />
            </Box>
          ) : !planNotFound && table.show ? (
            <Table
              auth={auth}
              table={table}
              statusOptions={statusOptions}
              handleExport={handleExport}
              handleImport={handleImport}
            />
          ) : (
            <NotFoundTemplate active={false} />
          ))}

        {!auth && importModal && (
          <ImportModal
            id={selected ?? plan_id}
            importModal={importModal}
            setImportModal={setImportModal}
            handleViewPlan={handleViewPlan}
          />
        )}
      </Box>
    </Container>
  );
}
